import React from 'react';
import { formatCPF, formatPhone, formatDate } from '@/lib/formatters';

interface PDFWrapperProps {
  patient: {
    name: string;
    cpf: string | null;
    data_nascimento: string | null;
    celular: string | null;
    email: string | null;
  } | null;
  professional: {
    full_name: string | null;
    cpf: string | null;
    health_area: string | null;
    phone: string | null;
  } | null;
  stats: {
    subcategoryCount: number;
    totalMeasurements: number;
    outOfRange: number;
    normal: number;
  };
  children: React.ReactNode;
}

export function PatientIndicatorsPDFWrapper({
  patient,
  professional,
  stats,
  children
}: PDFWrapperProps) {
  const now = new Date();
  
  return (
    <div 
      className="pdf-wrapper bg-white" 
      style={{ 
        fontFamily: 'system-ui, -apple-system, sans-serif',
        width: '180mm',
        maxWidth: '180mm',
        margin: '0 auto',
        padding: '10mm',
        boxSizing: 'border-box'
      }}
    >
      <style dangerouslySetInnerHTML={{__html: `
        .pdf-wrapper {
          background: white;
          width: 180mm;
          max-width: 180mm;
          padding: 10mm;
          box-sizing: border-box;
        }
        
        .pdf-header {
          border-bottom: 3px solid #0891b2;
          padding-bottom: 20px;
          margin-bottom: 30px;
          background: linear-gradient(to bottom, #ffffff, #f9fafb);
          padding: 20px;
          border-radius: 8px;
        }
        
        .pdf-footer {
          border-top: 3px solid #0891b2;
          padding-top: 16px;
          margin-top: 30px;
          font-size: 12px;
          color: #374151;
          background: #f9fafb;
          padding: 20px;
          border-radius: 8px;
        }
        
        .pdf-chart {
          page-break-inside: avoid;
          margin-bottom: 30px;
          padding: 20px;
          background: #fafafa;
          border: 1px solid #e5e5e5;
          border-radius: 8px;
          min-height: 450px;
        }
        
        .pdf-chart-container {
          width: 100%;
          height: 350px;
          margin-top: 16px;
        }
        
        .pdf-chart-title {
          font-size: 20px;
          font-weight: 700;
          color: #1f2937;
          margin-bottom: 12px;
        }
        
        .pdf-chart-value {
          font-size: 28px;
          font-weight: 700;
          color: #0891b2;
        }
        
        .pdf-chart-ref-range {
          font-size: 18px;
          font-weight: 600;
          color: #374151;
        }
        
        @media print {
          .pdf-chart {
            page-break-before: always;
          }
          .pdf-chart:first-child {
            page-break-before: avoid;
          }
        }
      `}} />

      {/* Cabeçalho */}
      <div className="pdf-header">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-2xl font-bold mb-2" style={{ color: '#0891b2' }}>
              Relatório de Indicadores de Saúde
            </h1>
            <div className="space-y-1 text-sm">
              <p className="font-semibold text-lg" style={{ color: '#1f2937' }}>{patient?.name || 'Paciente'}</p>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1" style={{ color: '#4b5563' }}>
                <p><span className="font-medium">CPF:</span> {patient?.cpf ? formatCPF(patient.cpf) : '-'}</p>
                <p><span className="font-medium">Nascimento:</span> {patient?.data_nascimento ? formatDate(patient.data_nascimento) : '-'}</p>
                <p><span className="font-medium">Telefone:</span> {patient?.celular ? formatPhone(patient.celular) : '-'}</p>
                <p><span className="font-medium">Email:</span> {patient?.email || '-'}</p>
              </div>
            </div>
          </div>
          <div className="text-right text-xs" style={{ color: '#6b7280' }}>
            <p className="font-medium">Data de emissão:</p>
            <p className="font-semibold text-base" style={{ color: '#374151' }}>
              {now.toLocaleDateString('pt-BR')}
            </p>
            <p>{now.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}</p>
          </div>
        </div>
      </div>

      {/* Estatísticas Gerais */}
      <div className="mb-8">
        <h2 className="text-lg font-semibold mb-4" style={{ color: '#0891b2' }}>
          Resumo Geral
        </h2>
        <div className="grid grid-cols-4 gap-3">
          <div className="bg-gradient-to-br from-cyan-50 to-cyan-100 p-3 rounded-lg border-2 border-cyan-200">
            <p className="text-xs text-gray-600 mb-1 font-medium">Indicadores Medidos</p>
            <p className="text-2xl font-bold" style={{ color: '#0891b2' }}>
              {stats.subcategoryCount}
            </p>
          </div>
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-3 rounded-lg border-2 border-blue-200">
            <p className="text-xs text-gray-600 mb-1 font-medium">Total de Medições</p>
            <p className="text-2xl font-bold" style={{ color: '#0891b2' }}>
              {stats.totalMeasurements}
            </p>
          </div>
          <div className="bg-gradient-to-br from-red-50 to-red-100 p-3 rounded-lg border-2 border-red-200">
            <p className="text-xs text-gray-600 mb-1 font-medium">Fora da Faixa</p>
            <p className="text-2xl font-bold" style={{ color: '#ef4444' }}>
              {stats.outOfRange}
            </p>
          </div>
          <div className="bg-gradient-to-br from-green-50 to-green-100 p-3 rounded-lg border-2 border-green-200">
            <p className="text-xs text-gray-600 mb-1 font-medium">Valores Normais</p>
            <p className="text-2xl font-bold" style={{ color: '#10b981' }}>
              {stats.normal}
            </p>
          </div>
        </div>
      </div>

      {/* Conteúdo (gráficos) */}
      <div>
        {children}
      </div>

      {/* Rodapé */}
      <div className="pdf-footer">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="font-semibold text-sm mb-2" style={{ color: '#374151' }}>
              Profissional Responsável
            </p>
            <p className="font-medium text-base" style={{ color: '#1f2937' }}>{professional?.full_name || 'Não informado'}</p>
            <p className="text-sm">{professional?.health_area || 'Área não informada'}</p>
            <p className="text-sm">CPF: {professional?.cpf ? formatCPF(professional.cpf) : 'Não informado'}</p>
            <p className="text-sm">Contato: {professional?.phone ? formatPhone(professional.phone) : 'Não informado'}</p>
          </div>
          <div className="text-right">
            <p className="text-xs mt-4" style={{ color: '#9ca3af' }}>
              Documento gerado automaticamente pela plataforma
            </p>
            <p className="text-xs mt-1 font-medium" style={{ color: '#6b7280' }}>
              Este relatório contém {stats.subcategoryCount} indicadores de saúde monitorados
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
